/**
 * The practice areas a firm works in, and the one spelling of each.
 * @file src/shared/practice-areas.ts
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 * A form's matter type was free text with `personal-injury` as a placeholder,
 * and it is not decoration: the type decides which facts a draft matter asks
 * for (matter-fields.ts), which checklist a document request starts from
 * (document-checklists.ts), which stages and which agreements are on offer,
 * and how every report breaks down. Matching is by substring, so `PI` or
 * `auto accident` matched nothing and the firm silently lost all of it — and
 * two people typing `personal injury` and `personal-injury` split one
 * practice area into two that no report ever adds back together.
 *
 * So the firm picks from a list. The list is theirs — the twenty-one below
 * are only what a new firm starts with, and they are exactly the types the
 * form library's own templates carry, so starting a form from the library
 * lands on a practice area the firm already has rather than inventing a
 * twenty-second.
 *
 * ── Values are slugs, labels are words ────────────────────────────────────
 * `injury`, not `Personal injury`. The slug is what is stored, matched and
 * sent to a case system; the label is what a human reads. A firm adding
 * "Workers' comp" gets `workers-comp` stored and their own words shown.
 *
 * ── Nothing here renames what a firm already typed ─────────────────────────
 * Values already on a form or a prospect are offered alongside the list
 * (see `mergePracticeAreas`), so turning free text into a list does not
 * orphan a single existing form.
 *
 * @llm-rule WHEN: Offering, storing or labelling a matter type / practice area
 * @llm-rule AVOID: Writing a matter type that did not come from `slugifyPracticeArea`
 * @llm-rule NOTE: Match types with `matchesMatterType`, never `includes` —
 *   a bare substring test makes `real-estate` a probate matter
 */

export interface PracticeArea {
  /** Stored, matched and sent onward. Lower case, hyphenated. */
  value: string;
  /** What a person reads. */
  label: string;
}

/**
 * What a new firm starts with — the eleven the form library is organised by.
 * A firm adds, removes and renames from Settings.
 */
export const DEFAULT_PRACTICE_AREAS: PracticeArea[] = [
  { value: 'general', label: 'General' },
  { value: 'personal-injury', label: 'Personal injury' },
  { value: 'medical-malpractice', label: 'Medical malpractice' },
  { value: 'workers-compensation', label: 'Workers’ compensation' },
  { value: 'wrongful-death', label: 'Wrongful death' },
  { value: 'mass-tort', label: 'Mass tort' },
  { value: 'civil-litigation', label: 'Civil litigation' },
  { value: 'family', label: 'Family' },
  { value: 'criminal', label: 'Criminal defense' },
  { value: 'traffic', label: 'Traffic' },
  { value: 'immigration', label: 'Immigration' },
  { value: 'employment', label: 'Employment' },
  { value: 'business', label: 'Business' },
  { value: 'real-estate', label: 'Real estate' },
  { value: 'landlord-tenant', label: 'Landlord & tenant' },
  { value: 'probate', label: 'Probate' },
  { value: 'estate-planning', label: 'Estate planning' },
  { value: 'guardianship', label: 'Guardianship' },
  { value: 'debt', label: 'Debt' },
  { value: 'bankruptcy', label: 'Bankruptcy' },
  { value: 'social-security', label: 'Social security & disability' },
];

/** The most a firm may keep. A list longer than this is a taxonomy problem. */
export const MAX_PRACTICE_AREAS = 40;

/**
 * One spelling, from whatever somebody typed.
 * "Personal Injury" and "personal injury" both become `personal-injury`.
 */
export function slugifyPracticeArea(input: string): string {
  return input
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** `personal-injury` → `Personal injury`, for a value nobody has named. */
function titleise(value: string): string {
  const words = value.replace(/-/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : value;
}

/**
 * What to call a value. The firm's own label wins; then the standard list;
 * then the slug read as words, so an unknown value is never shown raw.
 */
export function practiceAreaLabel(value: string, known: PracticeArea[] = DEFAULT_PRACTICE_AREAS): string {
  return known.find((a) => a.value === value)?.label ?? titleise(value);
}

/**
 * The options to offer: the firm's list, plus anything already in use.
 *
 * `configured` empty means the firm has not chosen, so they get the standard
 * eleven. `inUse` is every value already on a form or a prospect — offered
 * even when the firm has since removed it from their list, because the
 * alternative is a form whose own type is missing from its own picker.
 */
export function mergePracticeAreas(
  configured: string[] | null | undefined,
  inUse: string[] = [],
): PracticeArea[] {
  const base: PracticeArea[] = configured?.length
    ? configured.map((value) => ({ value, label: practiceAreaLabel(value) }))
    : [...DEFAULT_PRACTICE_AREAS];

  const seen = new Set(base.map((a) => a.value));
  for (const raw of inUse) {
    const value = slugifyPracticeArea(raw ?? '');
    if (!value || seen.has(value)) continue;
    seen.add(value);
    base.push({ value, label: practiceAreaLabel(value) });
  }
  return base;
}

/**
 * Does a matter type fall under a rule written for `matcher`?
 *
 * Hyphen boundaries, not bare substrings. The naive test this replaces —
 * `type.includes(m) || m.includes(type)` — read `real-estate` as an `estate`
 * matter, so a house purchase was asked for the name of the deceased and the
 * probate document checklist. Boundaries keep the intended matches (a rule
 * for `estate` still covers `estate-planning`) and drop the accidental ones.
 */
export function matchesMatterType(type: string | null | undefined, matcher: string): boolean {
  const t = (type ?? '').toLowerCase().trim();
  const m = matcher.toLowerCase().trim();
  if (!t || !m) return false;
  return t === m || t.startsWith(`${m}-`) || m.startsWith(`${t}-`);
}
